/**
 * Cat Chat Component
 * Main chat window for Mèo Học Bá
 */

import { useState, useRef, useEffect } from 'react';
import { X, Send, Minimize2, Brain, ChevronUp, ChevronDown } from 'lucide-react';
import { ChatMessage, TypingIndicator, type Message } from './ChatMessage';
import { findResponse, getWelcomeMessage, QUICK_ACTIONS } from '../../data/chatResponses';
import { useNavigate } from 'react-router-dom';

interface CatChatProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimize: () => void;
}

export function CatChat({ isOpen, onClose, onMinimize }: CatChatProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        text: getWelcomeMessage(),
        sender: 'cat',
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const [pendingAction, setPendingAction] = useState<{
    url: string;
    label: string;
    type?: 'navigate' | 'external';
  } | null>(null);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setPendingAction(null);
    setShowSuggestions(false);

    // Simulate typing delay
    setTimeout(
      () => {
        const { response, action } = findResponse(userMessage.text);

        const catMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response,
          sender: 'cat',
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, catMessage]);
        setIsTyping(false);

        // Set action if available
        if (
          action &&
          (action.type === 'navigate' || action.type === 'external') &&
          action.url &&
          action.label
        ) {
          setPendingAction({ url: action.url, label: action.label, type: action.type });
        }
      },
      800 + Math.random() * 700
    );
  };

  const handleActionClick = (url: string, type?: 'navigate' | 'external') => {
    const catMessage: Message = {
      id: Date.now().toString(),
      text:
        type === 'external'
          ? 'Tuyệt vời! Mình đã mở form cho bạn! Hãy điền thông tin để nhận thưởng nhé! 🎁'
          : 'Tuyệt vời! Mình đưa bạn đến ngay! 🚀',
      sender: 'cat',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, catMessage]);
    setPendingAction(null);

    setTimeout(() => {
      if (type === 'external') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }, 800);
  };

  const handleQuickAction = (message: string) => {
    // Special handling for CAT test action
    if (message.includes('làm bài test CAT')) {
      const catMessage: Message = {
        id: Date.now().toString(),
        text: 'Tuyệt vời! Mình sẽ đưa bạn đến trang làm bài CAT ngay! 🚀',
        sender: 'cat',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, catMessage]);

      setTimeout(() => {
        navigate('/huong-nghiep-cat?screen=instruction');
      }, 1500);
      setShowSuggestions(false);
      return;
    }

    setInputValue(message);
    setTimeout(() => handleSend(), 100);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 animate-scale-in'>
      <div className='w-[340px] sm:w-[380px] h-[500px] sm:h-[550px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700'>
        {/* Header */}
        <div className='bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-full bg-white/20 overflow-hidden'>
              <img
                src='/mascot/cat-wave.png'
                alt='Mèo Học Bá'
                className='w-full h-full object-cover'
              />
            </div>
            <div>
              <h3 className='font-bold text-white text-sm'>Mèo Học Bá</h3>
              <div className='flex items-center gap-1'>
                <span className='w-2 h-2 bg-green-400 rounded-full animate-pulse' />
                <span className='text-white/80 text-xs'>Online</span>
              </div>
            </div>
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={onMinimize}
              className='p-2 hover:bg-white/20 rounded-lg transition-colors'
              aria-label='Thu nhỏ'
            >
              <Minimize2 className='w-4 h-4 text-white' />
            </button>
            <button
              onClick={onClose}
              className='p-2 hover:bg-white/20 rounded-lg transition-colors'
              aria-label='Đóng'
            >
              <X className='w-4 h-4 text-white' />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className='flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900'>
          {messages.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          {isTyping && <TypingIndicator />}

          {/* Action Button */}
          {pendingAction && !isTyping && (
            <div className='flex justify-start animate-fade-in'>
              <button
                onClick={() => handleActionClick(pendingAction.url, pendingAction.type)}
                className='ml-10 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-medium rounded-full shadow-lg transition-all hover:scale-105'
              >
                {pendingAction.label}
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Collapsible Quick Actions */}
        <div className='border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            className='w-full px-4 py-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
          >
            <span>💡 Gợi ý nhanh</span>
            {showSuggestions ? (
              <ChevronDown className='w-4 h-4' />
            ) : (
              <ChevronUp className='w-4 h-4' />
            )}
          </button>
          {showSuggestions && (
            <div className='px-4 pb-2 flex flex-wrap gap-2'>
              {QUICK_ACTIONS.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.message)}
                  className='text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors text-gray-700 dark:text-gray-300'
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <div className='p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
          <div className='flex items-center gap-2'>
            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Nhập tin nhắn...'
              className='flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400'
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className='p-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 rounded-full transition-colors disabled:cursor-not-allowed'
              aria-label='Gửi'
            >
              <Send className='w-4 h-4 text-white' />
            </button>
          </div>
          <p className='text-[10px] text-gray-400 dark:text-gray-500 text-center mt-2 flex items-center justify-center gap-1'>
            <Brain className='w-3 h-3' />
            Phiên bản thông minh hơn của tớ sắp ra mắt~
          </p>
        </div>
      </div>

      {/* Custom animation */}
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default CatChat;
